'use client';

import React from 'react';
import { UseFormRegister, FieldValues, Path } from 'react-hook-form';
import { cn } from '@/lib/utils';
import { ChevronDown } from 'lucide-react';

interface Option {
  value: string;
  label: string;
}

interface FormSelectProps<T extends FieldValues>
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'name'> {
  label?: string;
  name: Path<T>;
  error?: string;
  register?: UseFormRegister<T>;
  options?: Option[];
  leftIcon?: React.ReactNode;
}

export function FormSelect<T extends FieldValues>({
  label,
  name,
  error,
  register,
  options = [],
  children,
  leftIcon,
  className,
  ...props
}: FormSelectProps<T>) {
  return (
    <div className="w-full space-y-1">
      {label && (
        <label
          htmlFor={name}
          className="block text-sm font-medium text-gray-700"
        >
          {label}
        </label>
      )}
      <div className="relative">
        {leftIcon && (
          <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center">
            {leftIcon}
          </div>
        )}

        <select
          id={name}
          autoComplete="off"
          {...(register ? register(name) : {})}
          className={cn(
            'form-select w-full appearance-none rounded-lg border-gray-300 bg-white/70 shadow-sm transition-all duration-200',
            'focus:border-primary-500 focus:ring-primary-500 sm:text-sm',
            leftIcon && 'pl-10',
            error && 'border-red-300 focus:border-red-500 focus:ring-red-500',
            className
          )}
          {...props}
        >
          {options.length > 0
            ? options.map(({ value, label }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))
            : children}
        </select>

        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
          <ChevronDown className="h-5 w-5 text-gray-400" />
        </div>
      </div>
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}
